import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiClientError } from '@/lib/api-client';

import type { RoleDto } from '@/features/settings/roles/types';

import {
  assignWorkPackage,
  createWorkPackage,
  getCompanyScope,
  listWorkPackages,
  updateCompanyScope,
  updateWorkPackage,
} from './api';
import type {
  CompanyScopeItem,
  WorkPackageAssignInput,
  WorkPackageCreateInput,
  WorkPackageDto,
  WorkPackageUpdateInput,
} from './types';

const WORK_PACKAGES_KEY = 'work-packages' as const;
const COMPANY_SCOPE_KEY = 'company-work-package-scope' as const;
const ROLES_KEY = 'roles' as const;

export const useWorkPackagesList = () => {
  return useQuery<WorkPackageDto[], ApiClientError>({
    queryKey: [WORK_PACKAGES_KEY, 'list'],
    queryFn: listWorkPackages,
    placeholderData: (prev) => prev,
  });
};

export const useCreateWorkPackage = () => {
  const qc = useQueryClient();
  return useMutation<WorkPackageDto, ApiClientError, WorkPackageCreateInput>({
    mutationFn: createWorkPackage,
    onSuccess: () => qc.invalidateQueries({ queryKey: [WORK_PACKAGES_KEY] }),
  });
};

export const useUpdateWorkPackage = () => {
  const qc = useQueryClient();
  return useMutation<WorkPackageDto, ApiClientError, { id: string; body: WorkPackageUpdateInput }>({
    mutationFn: ({ id, body }) => updateWorkPackage(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [WORK_PACKAGES_KEY] }),
  });
};

export const useAssignWorkPackage = () => {
  const qc = useQueryClient();
  return useMutation<RoleDto, ApiClientError, { id: string; body: WorkPackageAssignInput }>({
    mutationFn: ({ id, body }) => assignWorkPackage(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [ROLES_KEY] }),
  });
};

export const useCompanyScope = (companyId: string | undefined) => {
  return useQuery<CompanyScopeItem[], ApiClientError>({
    queryKey: [COMPANY_SCOPE_KEY, companyId],
    queryFn: () => getCompanyScope(companyId as string),
    enabled: !!companyId,
  });
};

export const useUpdateCompanyScope = () => {
  const qc = useQueryClient();
  return useMutation<
    CompanyScopeItem[],
    ApiClientError,
    { companyId: string; items: { work_package_id: string; enabled: boolean }[] }
  >({
    mutationFn: ({ companyId, items }) => updateCompanyScope(companyId, items),
    onSuccess: (_data, variables) =>
      qc.invalidateQueries({ queryKey: [COMPANY_SCOPE_KEY, variables.companyId] }),
  });
};
